/**
 * Client-side landing engagement + interaction-event collector — the v2 half
 * of the `/ig` bio-link pipeline (`./attribution.ts` is v1: cookieless visit
 * attribution — "who/where did this visitor come from"). This module answers
 * "what did they actually do on the page": engaged time, scroll depth,
 * clicks, form starts/submits, outbound links — feeding the new
 * `landing_events` table plus `landing_visits`' engagement columns
 * (engaged_ms/duration_ms/max_scroll_pct/last_seen_at) and the
 * /admin/traffic "live view".
 *
 * Browser-only (DOM APIs: sessionStorage, navigator.sendBeacon, fetch,
 * scroll/visibility events, setInterval/setTimeout) — no Node builtins, same
 * constraint as attribution.ts, so this is safe to import from any
 * 'use client' component without pulling server-only code into the browser
 * bundle.
 *
 * `createEngagementCore` is plain, DOM-free, pure logic (unit-testable with
 * an injected clock, no internal timers — see its own docstring for the
 * pause/resume accumulator model). `startEngagementTracking` /
 * `startClickTracking` are the DOM bindings that wire it to real browser
 * events and flush it to the backend.
 *
 * Related: [[knowledge/integrations]] [[rules/security]] · ./attribution.ts
 */

import { getVisitorId } from './consent';

/** sessionStorage key VisitBeacon writes to (contract 1's 200 `{id}` response). */
export const VISIT_ID_STORAGE_KEY = 'cmg_visit_id';

// =============================================================================
// PURE CORE — no DOM, no timers, fully testable with an injected clock.
// =============================================================================

/** Engaged time stops accruing once this long has passed with no activity. */
const IDLE_TIMEOUT_MS = 30_000;

export interface EngagementSnapshot {
  /** Total time spent visible AND active (idle/hidden gaps excluded), ms. */
  engagedMs: number;
  /** Wall time since the core was created, regardless of visibility/activity. */
  durationMs: number;
  /** Max scroll depth percent seen so far (0-100, monotonically increasing). */
  maxScrollPct: number;
  /**
   * Time-to-first-interaction: ms from core creation to the FIRST
   * click/scroll/keydown (see `markFirstInteraction`'s docstring for why
   * pointermove/touchstart don't count), or null until one has happened.
   * Latched — once set, never changes for this core's lifetime.
   */
  ttfiMs: number | null;
  /** Count of real visibility toggles (`setVisible` calls whose value actually flipped). */
  visSwitches: number;
}

export interface EngagementCore {
  /** Call on any real user interaction (pointer/keyboard/touch/click/scroll). */
  markActivity(): void;
  /** Call on document.visibilitychange (true = visible, false = hidden). */
  setVisible(visible: boolean): void;
  /** Call with the current scroll-depth percent; only the running max is kept. */
  setScrollPct(pct: number): void;
  /**
   * Call ONLY from click/scroll/keydown handlers (never pointermove/touchstart
   * — those already drive `markActivity`'s idle-timeout reset but are too
   * noisy/passive a signal for "did this visitor actually engage" per the v3
   * design contract). Latches `ttfiMs` on the first call only; every
   * subsequent call this core's lifetime is a no-op.
   */
  markFirstInteraction(): void;
  /** Read the current totals. Safe to call at any time (e.g. on a heartbeat). */
  snapshot(): EngagementSnapshot;
}

/**
 * Pure "pause/resume accumulator" engagement clock. No internal timers —
 * engaged time is settled lazily, only when `markActivity`/`setVisible`/
 * `snapshot` are called, by closing out the currently-open "engaged segment"
 * up to `now` (or up to the idle deadline, whichever is earlier). This keeps
 * the core a plain function of its call sequence, so it's trivially testable
 * with a fake `nowFn` and needs no `setInterval` of its own — the DOM binding
 * layer below supplies the periodic heartbeat that keeps it fresh.
 *
 * "Engaging" at any instant = visible AND (now - lastActivityAt) < 30s. This
 * is a pure per-instant predicate: reactivating visibility resumes accrual
 * immediately as long as the LAST recorded activity (from before the tab was
 * hidden, potentially) is still within the 30s window — there's no separate
 * "idle clock keeps running while hidden" rule, just the one predicate.
 */
export function createEngagementCore(nowFn: () => number): EngagementCore {
  const startedAt = nowFn();
  let visible = true;
  let lastActivityAt = startedAt;
  let engagedMs = 0;
  let maxScrollPct = 0;
  // Timestamp the current uninterrupted "engaged" segment began, or null when
  // not currently engaging (hidden, or idle past the 30s timeout). Starts
  // open: a fresh core is visible with "activity" at t=0 by construction.
  let segmentStart: number | null = startedAt;
  let ttfiMs: number | null = null;
  let visSwitches = 0;

  /** Close out [segmentStart, min(now, idleDeadline)) into `engagedMs`, then
   * either roll the segment forward (still active) or close it (idle timeout
   * reached in this window). */
  function settle(now: number): void {
    if (segmentStart === null) return;
    const idleDeadline = lastActivityAt + IDLE_TIMEOUT_MS;
    const settleTo = Math.min(now, idleDeadline);
    if (settleTo > segmentStart) {
      engagedMs += settleTo - segmentStart;
    }
    segmentStart = now >= idleDeadline ? null : now;
  }

  return {
    markActivity() {
      const now = nowFn();
      settle(now);
      lastActivityAt = now;
      if (visible) segmentStart = now;
    },
    setVisible(next: boolean) {
      const now = nowFn();
      settle(now);
      if (next !== visible) visSwitches += 1;
      visible = next;
      segmentStart = visible && now - lastActivityAt < IDLE_TIMEOUT_MS ? now : null;
    },
    setScrollPct(pct: number) {
      const clamped = Math.max(0, Math.min(100, Math.round(pct)));
      maxScrollPct = Math.max(maxScrollPct, clamped);
    },
    markFirstInteraction() {
      if (ttfiMs !== null) return; // latched — first call wins, for this core's lifetime
      ttfiMs = nowFn() - startedAt;
    },
    snapshot(): EngagementSnapshot {
      const now = nowFn();
      settle(now);
      return { engagedMs, durationMs: now - startedAt, maxScrollPct, ttfiMs, visSwitches };
    },
  };
}

// =============================================================================
// TRANSPORT — shared fire-and-forget beacon, same pattern as VisitBeacon's
// original sendVisitBeacon (sendBeacon primary, fetch keepalive fallback).
// Unlike the `/api/track/visit` call, `/api/track/engage` and
// `/api/track/event` always respond 204 — there is no response body to read,
// so sendBeacon (which cannot read responses) stays the right primary
// transport here.
// =============================================================================

// NOTE on param name: always a same-origin API path string literal from the
// two call sites below ('/api/track/engage', '/api/track/event') — never
// user/attacker-controlled input. Named `apiPath` (not `url`) deliberately:
// the security scanner's SSRF check (scripts/check-security.js checkSSRF)
// flags `fetch(url, ...)`/`fetch(endpoint, ...)`-shaped calls as a possible
// unvalidated-URL SSRF vector; that heuristic doesn't apply here since this
// function never receives anything but our own hardcoded endpoint constants.
function sendBeaconOrFetch(apiPath: string, payload: Record<string, unknown>): void {
  const body = JSON.stringify(payload);

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const queued = navigator.sendBeacon(apiPath, blob);
      if (queued) return;
    }
  } catch {
    // intentional: sendBeacon can throw in locked-down browser contexts
    // (some privacy extensions, restricted iframes) — fall through to fetch.
  }

  fetch(apiPath, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // intentional: engagement/event beacons are best-effort telemetry, not a
    // product feature the visitor depends on. A failed network request here
    // (offline, ad-blocker, CSP, flaky connection) must never surface an
    // error to the visitor or generate error-reporting noise — the
    // /admin/traffic dashboard simply undercounts by one signal.
  });
}

function readVisitId(): string | null {
  try {
    return sessionStorage.getItem(VISIT_ID_STORAGE_KEY);
  } catch {
    // intentional: sessionStorage unavailable (private mode, storage quota) —
    // nothing to attribute engagement/events to from here.
    return null;
  }
}

// =============================================================================
// ENGAGEMENT DOM BINDING
// =============================================================================

const SCROLL_THROTTLE_MS = 500;
const HEARTBEAT_MS = 15_000;

/** Module-level guard so a React strict-mode double-mount never double-binds. */
let engagementStarted = false;

function computeScrollPct(): number {
  const doc = document.documentElement;
  const scrollable = Math.max(1, doc.scrollHeight);
  return Math.min(100, Math.round(((window.scrollY + window.innerHeight) / scrollable) * 100));
}

function snapshotsEqual(a: EngagementSnapshot | null, b: EngagementSnapshot): boolean {
  return (
    a !== null &&
    a.engagedMs === b.engagedMs &&
    a.durationMs === b.durationMs &&
    a.maxScrollPct === b.maxScrollPct &&
    a.ttfiMs === b.ttfiMs &&
    a.visSwitches === b.visSwitches
  );
}

/**
 * Binds `createEngagementCore` to real browser events and flushes it to
 * POST /api/track/engage (contract 2). Listeners + the core start
 * immediately regardless of whether `sessionStorage[cmg_visit_id]` exists
 * yet — unlike a bind-once read, `flush()` below reads it FRESH each time
 * (mirroring `trackEvent`'s fresh `readVisitId()` call further down this
 * file) and silently skips the send while it's absent, WITHOUT losing
 * anything: the core keeps accumulating engaged time/scroll underneath
 * regardless, so whatever accrued before the id landed is sent on the first
 * flush after it does. This is what makes it safe for VisitBeacon to start
 * this on the very first page of a session even though its own visit-id
 * fetch is still in flight — a prior bind-once-at-start read meant
 * navigating away before that fetch resolved permanently zeroed out the
 * rest of the session's engagement data (no id was ever loaded to attribute
 * to, and this only runs once per session's first tracked page).
 *
 * Returns a cleanup function (always — a no-op when nothing was bound), so
 * callers can treat the return value uniformly.
 */
export function startEngagementTracking(): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  if (engagementStarted) return () => {};

  engagementStarted = true;

  const core = createEngagementCore(() => Date.now());
  let lastFlushed: EngagementSnapshot | null = null;

  function flush(): void {
    // Fresh read (not captured once at bind time, see docstring above) — an
    // absent id here just means VisitBeacon's visit POST hasn't resolved
    // yet; skip this send and try again next flush, without touching
    // `lastFlushed` so the first successful send afterward isn't mistaken
    // for a no-op duplicate.
    const visitId = readVisitId();
    if (!visitId) return; // nothing to attribute to YET — see docstring above

    const snap = core.snapshot();
    if (snapshotsEqual(lastFlushed, snap)) return; // unchanged — skip the write
    lastFlushed = snap;
    sendBeaconOrFetch('/api/track/engage', {
      visitId,
      engagedMs: snap.engagedMs,
      durationMs: snap.durationMs,
      maxScrollPct: snap.maxScrollPct,
      viewportW: window.innerWidth,
      // ttfiMs is written once server-side (COALESCE keep-first) — safe to
      // keep resending the same latched value on every later heartbeat.
      // Omitted entirely (not `null`) until the first qualifying interaction,
      // matching the API contract's `ttfiMs?: int` (optional, not nullable).
      ...(snap.ttfiMs !== null ? { ttfiMs: snap.ttfiMs } : {}),
      visSwitches: snap.visSwitches,
      // v3 bug fix: read FRESH at flush time, never bound once — a visitor
      // who accepts the consent banner mid-session (after this page's visit
      // row was already sent with no visitor_id) would otherwise lose that
      // entire first session's identity retrofit forever (the exact class
      // that made VisitBeacon's own bind-once visitId read a data-loss bug in
      // v2 — see this file's `readVisitId`/`flush` docstring above). `?? undefined`
      // (not a conditional spread) is deliberate: `JSON.stringify` drops an
      // `undefined`-valued key entirely, matching the API's optional-uuid
      // contract exactly, same effect as the conditional-spread pattern used
      // for `ttfiMs` above. The server (POST /api/track/engage) is what
      // actually performs the retrofit — see its docstring.
      visitorId: getVisitorId() ?? undefined,
    });
  }

  let scrollThrottleTimer: ReturnType<typeof setTimeout> | null = null;
  function handleScroll(): void {
    if (scrollThrottleTimer) return;
    scrollThrottleTimer = setTimeout(() => {
      scrollThrottleTimer = null;
      core.setScrollPct(computeScrollPct());
      // Scrolling is real engagement (a visitor reading down the page), not
      // just a passive measurement — it also resets the idle clock.
      core.markActivity();
      core.markFirstInteraction();
    }, SCROLL_THROTTLE_MS);
  }

  function handleActivity(): void {
    core.markActivity();
  }

  // Distinct from handleActivity: click/keydown count toward
  // time-to-first-interaction (ttfiMs), pointermove/touchstart don't — see
  // `markFirstInteraction`'s docstring on EngagementCore for why.
  function handleFirstInteractionActivity(): void {
    core.markActivity();
    core.markFirstInteraction();
  }

  function handleVisibilityChange(): void {
    core.setVisible(document.visibilityState === 'visible');
    if (document.visibilityState !== 'visible') flush();
  }

  function handlePageHide(): void {
    flush();
  }

  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('pointermove', handleActivity, { passive: true });
  window.addEventListener('keydown', handleFirstInteractionActivity, { passive: true });
  window.addEventListener('touchstart', handleActivity, { passive: true });
  window.addEventListener('click', handleFirstInteractionActivity, { passive: true });
  document.addEventListener('visibilitychange', handleVisibilityChange, { passive: true });
  window.addEventListener('pagehide', handlePageHide, { passive: true });

  const heartbeat = setInterval(() => {
    if (document.visibilityState === 'visible') flush();
  }, HEARTBEAT_MS);

  return () => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('pointermove', handleActivity);
    window.removeEventListener('keydown', handleFirstInteractionActivity);
    window.removeEventListener('touchstart', handleActivity);
    window.removeEventListener('click', handleFirstInteractionActivity);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', handlePageHide);
    clearInterval(heartbeat);
    if (scrollThrottleTimer) clearTimeout(scrollThrottleTimer);
    flush(); // final flush on cleanup (e.g. client-side route change)
    engagementStarted = false;
  };
}

// =============================================================================
// EVENTS — POST /api/track/event (contract 3)
// =============================================================================

// v3 additions (design contract's landing_events.event_type CHECK, full set):
// page_view (VisitBeacon), web_vital/rage_click/dead_click/copy/form_field
// (Lane COLLECT's collectors.ts, which calls this same trackEvent internally
// per its cross-lane contract), link_click (server-side /go/[slug] insert —
// included here too so the union matches the DB CHECK exactly, even though
// no client call site uses it today).
export type TrackEventType =
  | 'click'
  | 'section_view'
  | 'form_start'
  | 'form_submit'
  | 'outbound'
  | 'page_view'
  | 'web_vital'
  | 'rage_click'
  | 'dead_click'
  | 'copy'
  | 'form_field'
  | 'link_click';

export interface TrackEventMeta {
  href?: string;
  section?: string;
  label?: string;
  /** Page path — web_vital/rage_click/dead_click/copy/form_field/page_view all carry this. */
  path?: string;
  /** Numeric metric value (web_vital). */
  value?: number;
  /** Short rating/status string, e.g. web-vitals' 'good'|'needs-improvement'|'poor' (≤16 chars). */
  rating?: string;
  /** Short element identifier (web_vital's worst element, rage/dead click selector). */
  element?: string;
  /** Short state string, e.g. form_field's 'filled'|'empty' — NEVER a field value. */
  state?: string;
}

/** Contract 6's event_name slug shape. */
const EVENT_SLUG_PATTERN = /^[a-z0-9_.-]{1,64}$/;

/**
 * Sanitize a raw event-name candidate into the contract-6 slug shape
 * (`/^[a-z0-9_.-]{1,64}$/`): lowercase, any invalid character (including
 * spaces and non-ASCII/diacritics) becomes `-`, then truncated to 64 chars.
 * Returns null when the result still doesn't match (currently only the
 * empty-string case, since every other character is coercible) — callers
 * drop the event silently rather than send a malformed name.
 */
export function toEventSlug(raw: string): string | null {
  const slug = raw
    .toLowerCase()
    .replace(/[^a-z0-9_.-]/g, '-')
    .slice(0, 64);
  return EVENT_SLUG_PATTERN.test(slug) ? slug : null;
}

/**
 * Fire-and-forget interaction event (clicks, form start/submit, outbound
 * links, section views). Attaches the stored visit id when present (contract
 * 3 — `visitId` is optional, so an absent id still sends a valid event).
 * Invalid/empty names are dropped silently via `toEventSlug`.
 */
export function trackEvent(type: TrackEventType, name: string, meta?: TrackEventMeta): void {
  if (typeof sessionStorage === 'undefined') return;
  const slug = toEventSlug(name);
  if (!slug) return;

  const payload: Record<string, unknown> = { type, name: slug };
  const visitId = readVisitId();
  if (visitId) payload.visitId = visitId;
  if (meta) payload.meta = meta;

  sendBeaconOrFetch('/api/track/event', payload);
}

// =============================================================================
// DELEGATED CLICK TRACKING — data-track attributes + outbound-link detection.
// =============================================================================

/** Module-level guard so a React strict-mode double-mount never double-binds. */
let clickTrackingStarted = false;

/**
 * ONE delegated, capture-phase click listener on `document`:
 *   (a) closest('[data-track]') -> trackEvent('click', <attr value>)
 *   (b) else closest('a[href]') resolving to a different host over http(s)
 *       -> trackEvent('outbound', <lowercased hostname>, { href })
 * Returns a cleanup function (always).
 */
export function startClickTracking(): () => void {
  if (typeof document === 'undefined') return () => {};
  if (clickTrackingStarted) return () => {};
  clickTrackingStarted = true;

  function handleClick(e: MouseEvent): void {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const trackEl = target.closest('[data-track]');
    if (trackEl) {
      const attrValue = trackEl.getAttribute('data-track');
      if (attrValue) trackEvent('click', attrValue);
      return;
    }

    const anchor = target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;

    let url: URL;
    try {
      url = new URL(anchor.href);
    } catch {
      // intentional: malformed/relative href resolved to nothing usable —
      // nothing to track.
      return;
    }
    const isHttp = url.protocol === 'http:' || url.protocol === 'https:';
    if (isHttp && url.host !== window.location.host) {
      trackEvent('outbound', url.hostname.toLowerCase(), { href: url.href.slice(0, 256) });
    }
  }

  document.addEventListener('click', handleClick, { capture: true, passive: true });

  return () => {
    document.removeEventListener('click', handleClick, { capture: true });
    clickTrackingStarted = false;
  };
}
